import type { ReactElement } from "react";

import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { resolveTenantDashboardPath } from "@/lib/tenantNavigation";

interface BackButtonProps {
  to?: string;
  text?: string;
  variant?: "default" | "ghost" | "outline";
  className?: string;
}

const DEFAULT_BASE_URL = "http://localhost";

const toRelative = (url: URL) => `${url.pathname}${url.search}${url.hash}`;

const ensureDemoQuery = (path: string) => {
  try {
    const url = new URL(path, DEFAULT_BASE_URL);
    if (url.searchParams.get("mode") === "demo") {
      return toRelative(url);
    }
    url.searchParams.set("mode", "demo");
    return toRelative(url);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn("[BackButton] unable to normalize demo path", error);
    }
    return path.includes("?") ? `${path}&mode=demo` : `${path}?mode=demo`;
  }
};

export const BackButton = ({
  to,
  text = "Back",
  variant = "ghost",
  className = "",
}: BackButtonProps): ReactElement => {
  const navigate = useNavigate();
  const { subdomain } = useParams();
  const [searchParams] = useSearchParams();
  const isDemoMode = searchParams.get("mode") === "demo";

  const handleBack = () => {
    if (!to) {
      window.history.back();
      return;
    }

    let destination = resolveTenantDashboardPath(to);

    if (isDemoMode) {
      if (subdomain && destination.startsWith("/dashboard")) {
        destination = `/r/${subdomain}/dashboard`;
      }
      destination = ensureDemoQuery(destination);
    }

    navigate(destination);
  };

  return (
    <Button
      variant={variant}
      size="sm"
      onClick={handleBack}
      className={cn("text-muted-foreground hover:text-foreground", className)}
    >
      <ArrowLeft className="w-4 h-4 mr-2" />
      {text}
    </Button>
  );
};
